import { useContext, useMemo } from 'react';
import { gql } from '@apollo/client';
import type { Brand } from '@apiTypes';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import useCancelableQuery from '../../core/hooks/apollo/useCancelableQuery';
import PreorderModeContext from '../../preorder/PreorderModeContext';
import filterBrands from '../util/filterBrands';
import sortByName from '../util/sortByName';
import getFirstLetters from '../util/getFirstLetters';

const useBrands = ({
    query,
    withKeyDealers,
    skip,
    startLetter,
}: {
    query?: string;
    withKeyDealers?: boolean;
    skip?: boolean;
    startLetter?: string;
} = {}) => {
    const { store } = useStoreAndLanguageParams();
    const {
        productsFilters: { includePreorderSeasonId, preorderSeasonId },
    } = useContext(PreorderModeContext);

    const {
        data: { brands } = { brands: [] },
        loading,
        error,
    } = useCancelableQuery<{
        brands: Brand[];
    }>(
        gql`
            query brands(
                $store: ID!
                $preorderSeasonId: Int
                $includePreorderSeasonId: Int
                $withKeyDealers: Boolean
            ) {
                brands(
                    store: $store
                    preorderSeasonId: $preorderSeasonId
                    includePreorderSeasonId: $includePreorderSeasonId
                    withKeyDealers: $withKeyDealers
                ) {
                    name
                    logo
                    slug
                    customLogo
                }
            }
        `,
        {
            variables: {
                store,
                preorderSeasonId,
                includePreorderSeasonId,
                withKeyDealers,
            },
            fetchPolicy: 'cache-first',
            skip,
        }
    );

    if (error) {
        throw error;
    }

    const sortedBrands = useMemo(() => sortByName(brands), [brands]);

    const filteredBrands = useMemo(
        () => filterBrands(sortedBrands, query ?? '', startLetter),
        [query, sortedBrands, startLetter]
    );

    const availableLetters = useMemo(
        () => getFirstLetters(filteredBrands),
        [filteredBrands]
    );

    return {
        brands: filteredBrands,
        loading,
        availableLetters,
    };
};

export default useBrands;
